<template>
  <div class="stations">

    <div class="map-controls">
      <transition name="slide-in-right">
        <station-list
          :stations="extendedStations"
          :selectedStationId="selectedStation.id || ''"
          :filter="filter"
          v-if="showList && !showScanner"
          @close="showList = false"
          @select="selectedStation = $event"
          @filter="filter = $event"
          v-model="search" />
      </transition>

      <transition name="fade-right">
        <div class="control-buttons" v-if="!showList && !showScanner">
          <el-button class="safe-top round-button" type="primary" icon="el-icon-more" @click="showList = true" circle></el-button>
        </div>
      </transition>
    </div>

    <transition name="fade-right">
      <div class="scanner" v-if="scanner && !showScanner">
        <el-button class="round-button" type="primary" @click="startScanner" circle>
          <fa v-if="!loadingScanner" icon="qrcode" />
          <fa v-else icon="circle-notch" class="fa-spin" />
          </el-button>
      </div>
    </transition>

    <el-row :gutter="0">
      <el-col :lg="24">
        <div class="full-content-height">
          <q-r-scanner v-if="showScanner" @close="showScanner = false" @loaded="loadingScanner = false" />
          <div v-show="!showScanner" class="map" :class="{ selected: mobile.showMap }">
            <station-map
              showUserPosition
              :height="mapHeight"
              :options="mapOptions"
              :stations="extendedStations"
              :selected="selectedStation"
              @selected="selectedStation = $event"
              @unSelect="selectedStation = $event"/>
          </div>
        </div>
      </el-col>
    </el-row>

  </div>
</template>
<script>
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet'
import Search from '@/components/shared/Search'
import CollectionPicker from '@/components/shared/CollectionPicker'
import Station from './Station'
import StationUpsertForm from './UpsertForm'
import StationMap from './map/StationMap'
import Distance from './util/distance.js'
import StationList from './StationList'
import QRScanner from '@/components/native/QRScanner'
import Vue from 'vue'

export default {
  name: 'Stations',
  components: { LMap, LTileLayer, LMarker, Search, CollectionPicker, LPopup, StationUpsertForm, Station, StationMap, StationList, QRScanner },
  data () {
    return {
      loadingScanner: false,
      showScanner: false,
      lightsOn: false,
      animateClose: false,
      showList: false,
      stationIsChanging: false,
      popupOpen: false,
      showUpsertStationDialog: false,
      selectedStation: {
        id: null,
        organization: {
          country: {}
        }
      },
      loading: true,
      mapLoading: true,
      mapOptions: {
        center: null
      },
      search: '',
      filter: '@ALL',
      stations: [],
      locationCoordinates: '',
      mobile: {
        showMap: false
      }
    }
  },
  computed: {
    scanner () {
      return Vue.cordova && Vue.cordova.QRScanner
    },
    mapHeight () {
      if (this.$screen.mobile) {
        return 'calc(100vh - 50px)'
      }
    },
    isMobile () {
      return window.innerWidth < 480
    },
    stationSelected () {
      return this.selectedStation.id !== null
    },
    extendedStations () {
      const stations = this.stations.filter(item => {
        if (!item.hidden || item.member) {         
          if (this.filter === '@ALL') {
            if (item['name'].toLowerCase().indexOf(this.search.toLowerCase()) !== -1) {
              return item
            }
          }
          if (this.filter === '@MEMBER' && item.member) {
            if (item['name'].toLowerCase().indexOf(this.search.toLowerCase()) !== -1) {
              return item
            }
          }
          if (this.filter === '@!MEMBER' && !item.member) {
            if (item['name'].toLowerCase().indexOf(this.search.toLowerCase()) !== -1) {
              return item
            }
          }
        }
      })

      return stations.sort((a, b) => {
        return (a.statusType - b.statusType) + (a.public - b.public) + (a.distance - b.distance) + (!a.member - !b.member)
      })
    },
    isUserRoute () {
      return this.$route.name === 'organizationStations'
    }
  },
  watch: {
    showList (val) {
      setTimeout(() => {
        if (val) {
          this.animateClose = true
        } else {
          this.animateClose = false
        }
      }, 300)
    }
  },
  methods: {
    startScanner () {
      this.loadingScanner = true; 
      setTimeout(() => {
        this.showScanner = true
      }, 1000);
    },
    setCoordinates (coordinates) {
      this.locationCoordinates = coordinates.lat + ',' + coordinates.lng
    },
    handleCreated (data) {
      this.showUpsertStationDialog = false
      this.stations.unshift(data)
      Distance.getGeo(this.$root.userLocation, this.stations)
    },
    getStations () {
      this.$http.get(this.$api.stations.index)
        .then(response => {
          this.stations = response.data.filter(item => { 
            item.distance = Distance.setDistance(this.$root.userLocation, item.locationCoordinates)
            return item
          })
          
          if (this.$route.query.id) {
            const stationToSelect = this.stations.find(item => item.id === this.$route.query.id) 
            if (stationToSelect) {
              this.selectedStation = stationToSelect
              this.mapOptions.center = { lat: stationToSelect.lat, lng: stationToSelect.lng }
            }
          }
          this.loading = false
        }, _ => {
          this.$message.error('Could not load stations')
        })
    }
  },
  created () {
    this.$root.getUserOrganizations(this.getStations)
  }
}
</script>

<style lang="scss" scoped>
  //@import "../../../../node_modules/leaflet/dist/leaflet.css";

  .map {
    height: 100%;
  }

  .control-buttons {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    position: absolute;
    z-index: 9998;
  }

  .scanner {
    position: absolute;
    bottom: 0px;
    right: 0px;
    z-index: 999;
    padding: 20px;

    display: flex;
    justify-content: flex-end;

    button {
      &:last-child {
        margin-left: auto;
      }
    }
  }

  .map-controls {
    position: absolute;
    top: 0px;
    right: 0px;
    width: 100%;
    z-index: 999;
    padding: 20px;

    display: flex;
    justify-content: flex-end;

    button {
      &:last-child {
        margin-left: auto;
      }
    }
  }

  .stations {
    position: relative;

    @include breakpoint (xs) {
      height: calc(100vh - 110px);
    }
  }

  .content-container {
    position: absolute;
    width: 30%;
    height: 80%;
    right: 50px;
    z-index: 999;
    top: 50px;
  }

  .geolocation-loading {
    position: absolute;
    right: 15px;
    bottom: 15px;
    //background-color: rgba(#000, 0.2);
    font-size: 16px;
    border-radius: 1px;
    color: #303133;
    font-weight: 600;
    width: 210px;
    display: flex;
    justify-content: space-around;
    align-items: center;
    z-index: 999;

    svg {
      animation: spin 2s linear infinite;
    }

  }

  @keyframes spin {
    from {transform:rotate(0deg);}
    to {transform:rotate(360deg);}
  }

  .add-station-button {
    position: absolute;
    right: 15px;
    top: 75px;
    z-index: 999;
  }

  .search-and-controls {
    display: flex;
    align-items: center;
    padding: 20px 25px;
    box-shadow: 0px 2px 6px 0px rgba(0,0,0,0.025);
  }

  .station-search {
    padding-top: 20px;
    padding-bottom: 20px;
    padding-left: 25px;
  }

  .stations-container {
    min-height: 540px;
    width: 100%;
    overflow-y: scroll;

    @include breakpoint (xs) {
      height: 100vh;
    }

    &::-webkit-scrollbar { 
      display: none; 
    }

    h3 {
      border-top: 1px solid #F2F6FC;
      text-transform: none;
      font-size: 14px;
      margin: 0;
      font-weight: 400;
      padding: 15px 35px;
      color: #C0C4CC;
    }
  }

  .map-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 1;
    top: 0px;
    left: 0px;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity .2s ease-out;

    &.active {
      opacity: 0;
    }
  }

  .add-station-dialog {
    .el-dialog__body {
      padding: 0px !important;
    }
  }

  .map {
    width: 100%;
    background-color: gray;
    overflow: hidden;
    position: relative;

    @include breakpoint (xs) {
    
    }

    &.selected {
      @include breakpoint (xs) {
        bottom: 0px;
      }
    }

    .add-station {
      position: absolute;
      right: 15px;
      top: 15px;
      background-color: rgba(white, 0.95);
      border-radius: 4px;
      z-index: 1000;

      display: flex;
      justify-content: center;
      align-items: center;

      .add-station-coordinates {
        padding: 20px;
        width: 250px;
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        > * {
          margin-bottom: 10px;

          &:last-child {
            margin-bottom: 0px;
          }
        }

        p {
          width: 100%;
          text-align: center;
          margin-top: 0px;
          font-size: 14px;
          margin-bottom: 15px;
        }
      }

      .add-station-details {
        button {
          width: 100%;
        }
      }
    }
  }

  .station {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 15px 10px;
    cursor: pointer;

    &.active-station {
      background-color: whitesmoke;
    }

    &:hover {
      background-color: whitesmoke;
    }

    i {
      font-size: 18px;
      padding-right: 10px;
    }

    div {
      display: flex;
      flex-direction: column;
      p {
        font-size: 14px;
        margin: 0px;
      }

      span {
        font-size: 10px;
        color: gray;
      }
    }
  }

  .round-button {
    @include breakpoint (xs) {
      width: 50px;
      height: 50px;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/Stations.vue